import { Injectable } from '@angular/core'
import { InternationalizationService } from '../internationalization.service'
import { StorageService } from '../storage.service'

export type CurrencySignType = '$' | '₽' | ''
export type LanguagesTypes = 'en' | 'ru' | null
export type LanguageListType = { key: LanguagesTypes; descr: string }

@Injectable({
  providedIn: 'root'
})
export class I18nService extends InternationalizationService {
  key = 'language'
  language = 'en'

  private fixedLanguage: LanguagesTypes = null

  constructor(private storageService: StorageService) {
    super()
    this.detectInitLanguage()
  }

  freezeLanguage(lang: LanguagesTypes) {
    this.fixedLanguage = lang
    this.detectInitLanguage()
  }

  detectInitLanguage() {
    if (this.fixedLanguage !== null) {
      this.language = this.fixedLanguage
      return this.fixedLanguage
    }

    const langFromLocalStorage = this.storageService.load<string>(this.key)

    if (langFromLocalStorage) {
      this.language = langFromLocalStorage
    } else {
      // navigator.language - get language from browser
      navigator.language.slice(0, 2) === 'ru' ? (this.language = 'ru') : (this.language = 'en')
    }
    return this.language
  }

  isEng() {
    switch (this.language) {
      case 'en':
        return true
      case 'ru':
        return false
    }
  }

  getCurrencySign(): CurrencySignType {
    switch (this.language) {
      case 'en':
        return '$'
      case 'ru':
        return '₽'
      default:
        return ''
    }
  }

  setLanguage(language: string) {
    this.language = language
    this.storageService.save(this.key, this.language)
    this.currentLanguage.next(this.language)
  }

  getLanguageForPage() {
    return this.language === 'ru' ? ru : en
  }
}

export type LgType = typeof ru
type PluralizeBeatsParams = { count: number; isTotalBeatsCount: boolean }

const ru = {
  pluralRules: new Intl.PluralRules('ru'),
  pluralizeBeats({ count, isTotalBeatsCount }: PluralizeBeatsParams) {
    const form = this.pluralRules.select(count)
    switch (form) {
      case 'one':
        return isTotalBeatsCount ? `бит` : `${count} бит`
      case 'few':
        return isTotalBeatsCount ? `бита` : `${count} бита`
      case 'many':
        return isTotalBeatsCount ? `битов` : `${count} битов`
      default:
        return `${count} бит`
    }
  },
  pluralizeTotalPlays(count: number): string {
    const form = this.pluralRules.select(count)
    switch (form) {
      case 'one':
        return `проигрывание`
      case 'few':
        return `проигрывания`
      case 'many':
        return `проигрываний`
      default:
        return `проигрываний`
    }
  },
  pluralizeTotalFollowers(count: number): string {
    const form = this.pluralRules.select(count)
    switch (form) {
      case 'one':
        return `подписчик`
      case 'few':
        return `подписчика`
      case 'many':
        return `подписчиков`
      default:
        return `подписчиков`
    }
  },
  catalogPage: {
    headings: {
      allBeats: 'каталог - все биты',
      popularBeats: 'каталог - обратите внимание'
    },
    tabs: {
      tracks: 'Треки',

      producers: 'Продюсеры'
    }
  },
  freeBeatsPage: {
    headings: {
      freeBeats: 'Каталог - Бесплатные биты'
    }
  },
  documentTitles: {
    allBeatmakersCatalog: 'Рэп и Хип-Хоп биты. Самый большой каталог в России | Beatmaker.tv',
    freeBeats: 'Каталог - Бесплатные биты | Beatmaker.tv',
    setBeatmakerPageTitle(beatmakerName: string) {
      return `${beatmakerName} | Beatmaker.tv`
    },
    setBeatmakerCatalogPageTitle(beatmakerName: string) {
      return `${beatmakerName} | Каталог | Beatmaker.tv`
    }
  },
  header: {
    home: 'Главная',
    tracks: 'Биты',
    licenses: 'Лицензии и цены',
    goods: 'Товары',
    services: 'Услуги',
    contacts: 'Контакты',
    pages: 'Страницы сайта',
    cart: 'Корзина',
    /* bmstv */
    catalog: 'Каталог',
    login: 'Логин',
    favorites: 'Избранное',
    purchases: 'Покупки'
  },
  tracksBlock: {
    heading: 'Каталог',
    style: 'Жанр',
    mood: 'Настроение',
    bpm: 'BPM',
    bpmDescription: 'Ударов в минуту',
    usingSamples: 'Использование семплов',
    similarTracks: 'Вам также могут понравиться эти биты',
    share: 'Поделиться треком',
    shareLink: 'Ссылка на трек',
    downloadDrop: 'Скачать',
    addFavoriteDrop: 'В избранное',
    shareDrop: 'Поделиться'
  },
  producersBlock: {
    searchProducerPlaceholder: 'Имя продюсера'
  },
  sortTrackBy: {
    rating: {
      label: 'Рейтинг',
      freeBeatsOptions: {
        addedDateDesc: 'От новых к старым',
        addedDateAsc: 'От старых к новым',
        downloadsDesc: 'Популярное (по убыванию)',
        downloadsAsc: 'Популярное (по возрастанию)'
      },
      options: {
        newest: 'Новое',
        default: 'По умолчанию',
        top: 'Лучшее'
      }
    },
    style: {
      label: 'Жанр',
      allStylesOption: 'Все жанры'
    },
    mood: {
      label: 'Настроение',
      allMoodsOption: 'Все настроения'
    },
    tags: {
      label: 'Поиск по тегу',
      notFound: 'Теги не найдены',
      remove: 'Убрать тег'
    },
    beat: {
      label: 'Поиск по биту'
    },
    beatmaker: {
      label: 'Поиск по битмейкеру',
      notFound: 'Битмейкеры не найдены'
    },
    key: {
      label: 'Тональность',
      allOption: 'Любая тональность'
    },
    trackType: {
      label: 'Тип трека',
      allOption: 'Любой тип'
    },
    instruments: {
      label: 'Инструменты'
    },
    bpm: {
      label: 'BPM'
    },
    price: {
      label: 'Цена'
    },
    range: {
      from: 'от',
      to: 'до'
    },
    multiSelectMode: {
      and: 'все',
      or: 'любой'
    },
    clearFilter: 'Сбросить фильтр',
    mobileFilters: {
      toggle: 'Фильтры'
    },
    adminSettings: {
      label: 'Сортировка битов',
      options: {
        default: 'По умолчанию (как в админке битов)',
        top: 'По рейтингу (начиная с лучшего)'
      }
    }
  },
  youtubeBlock: {
    heading: 'Видео на Youtube'
  },
  licenseBlock: {
    heading: 'Цены и условия лицензий',
    getSlogan(buyedLicenseCount: number, bonusLicenseCount: number) {
      return `Купи ${buyedLicenseCount} и получи еще ${bonusLicenseCount} в подарок`
    },
    noLicenseInfo: 'Нет информации о лицензии'
  },
  goodsBlock: {
    heading: 'Товары'
  },
  servicesBlock: {
    heading: 'Услуги'
  },
  contactsBlock: {
    heading: 'Контакты',
    getDescription(beatmakerName: string) {
      return `Свяжитесь с ${beatmakerName}, используя контактную форму или ссылки социальных сетей ниже`
    },
    requiredFieldDescription: 'Звёздочкой* отмечены поля, обязательные для заполнения перед отправкой сообщения',
    socNetwork: 'Социальные сети'
  },
  newsletterBlock: {
    heading: 'Рассылка новостей',
    getDescription(beatmakerName: string) {
      return `Подпишитесь на обновления ${beatmakerName}, чтобы узнавать о появлении новых битов и проходящих акциях`
    }
  },
  subscriptionBlock: {
    select1: '1 месяц',
    select2: '3 месяца',
    select3: '1 год',
    select4: '3 года'
  },
  footer: {
    getDescription(year: number) {
      return `Beatmaker.tv, ${year}. Все права защищены`
    },
    home: 'Главная',
    tracks: 'Биты',
    licenses: 'Лицензии',
    goods: 'Товары',
    services: 'Услуги',
    contacts: 'Контакты',
    pages: 'Страницы',
    info: 'Информация',
    'technical-support': 'Техподдержка',
    'user-agreement': 'Пользовательское соглашение',
    'cookie-policy': "Политика 'cookie'",
    /* bmstv */
    catalog: 'Каталог',
    'sign-in': 'Страница логинизации',
    'sign-up': 'Страница регистрации',
    'account/favorites': 'Любимые биты',
    'account/purchases': 'Купленные биты',
    language: {
      title: 'Язык',
      ru: 'Русский',
      en: 'Английский'
    }
  },
  cartPage: {
    cart: 'Корзина',
    payText: 'К оплате',
    amountDiscountText: 'Сумма с учетом скидок',
    orderText: 'Ваш заказ',
    coupon: 'Промокод',
    sum: 'Сумма',
    discount: 'Скидка',
    couponErrorEmpty: 'Купон не может быть пустым',
    couponErrorInvalid: 'Неверный купон',
    couponSuccessMessage: 'Купон успешно применен',
    emptyCart: 'В вашей корзине пока ничего нет',
    trackNotAvailable: 'Бит не доступен',
    beatAddedToCart: 'Бит добавлен в корзину'
  },
  button: {
    buy: 'Купить',
    download: 'Скачать',
    showMore: 'Показать больше',
    showProducts: 'Показать вcе товары',
    showServices: 'Показать вcе услуги',
    toChannel: 'Перейти на канал',
    toBeatList: 'К списку битов',
    toPayment: 'Перейти к оплате',
    toLogin: 'На страницу логина',
    moreDetails: 'Подробнее',
    sendMessage: 'Отправить сообщение',
    subscribeNewsletter: 'Подписаться на рассылку',
    payPurchase: 'Оплатить покупку',
    confirm: 'Подтвердить и оформить',
    apply: 'Применить',
    addToCart: 'Добавить в корзину',
    goToCart: 'В корзину',
    leaveComment: 'Оставить комментарий',
    priceFrom: 'Oт',
    understand: 'Все понятно',
    copyLink: 'Копировать ссылку',
    linkCopied: 'Ссылка скопирована',
    save: 'Сохранить',
    backToTracksPage: 'Вернуться на страницу треков',
    updatePassword: 'Обновить пароль',
    updateEmail: 'Обновить email',
    resetPassword: 'Сбросить пароль',
    backToHome: 'На главную страницу',
    search: 'Поиск'
  },
  forms: {
    name: 'Ваше имя*',
    fullName: 'ФИО*',
    email: 'Ваша почта*',
    message: 'Ваше сообщение*',
    password: 'Ваш пароль*',
    passportData: 'Паспортные данные',
    waysOfPayment: 'Способы оплаты',
    setEmptyErrorMessage(field: string) {
      return `Поле ${field} не должно быть пустым`
    },
    invalidEmail: 'Введите корректный email',
    successMessage:
      'Отлично. Ваше сообщение успешно отправлено. Владелец страницы свяжется с вами в ближайшее время. Спасибо!',
    subscribeMessage: 'Вы успешно подписались на новости',
    alreadySubscribeMessage: 'Вы уже подписаны',
    newPassword: 'Новый пароль',
    currentPassword: 'Текущий пароль',
    newEmail: 'Новый email',
    error: 'Данные формы не действительны, проверьте все поля формы на правильность',
    passwordsDontMatch: 'Пароли  не совпадают',
    passwordSuccessfullyChanged: 'Пароль успешно изменен'
  },
  confirmationOrderPopup: {
    checkout: 'Оформить заказ',
    total: 'Всего',
    agreeLicenseTerm: 'Оплачивая товар вы соглашаетесь с условиями лицензии',
    paymentSystem: 'Выберите платежную систему',
    unknownError:
      'Что то пошло не так. Проверьте еще раз правильность ввода ваших персональных данных. Если данные введены верно, значит свяжитесь с администрацией сайта'
  },
  commentsBlock: {
    trackComments: 'Комментариев к треку'
  },
  textarea: {
    message: 'Введите сообщение'
  },
  availablePage: {
    title: 'Страница не доступна',
    descr:
      'Приносим извинения за неудобства. Похоже, вы пытаетесь получить доступ к странице, которая была удалена или вообще не существовала'
  },
  pageNotFound: {
    title: 'Страница не найдена',
    descr:
      'Приносим извинения за неудобства. Похоже, вы пытаетесь получить доступ к странице, которая была удалена или вообще не существовала'
  },
  noMatchSearch: {
    result: 'Нет подходящих результатов поиска',
    action: 'Проверьте орфографию или попробуйте удалить фильтры'
  },
  cookiePolicyPopup: {
    description:
      'Этот веб-сайт использует файлы cookie для улучшения взаимодействия с пользователем. Используя файлы cookie, вы соглашаетесь на использование всех файлов cookie в соответствии с нашей Политикой использования файлов cookie.',
    btnAccept: 'Разрешить',
    btnReadMore: 'Подробнее'
  },
  colorCustomizer: {
    designPage: 'Дизайн страницы',
    textColor: 'Цвет текста',
    bgColor: 'Цвет фона',
    borderColor: 'Цвет границы',
    themeSelect: {
      title: 'Выберите тему',
      themes: {
        blackRed: 'Черно-красная тема',
        light: 'Светлая тема',
        blackOrange: 'Черно-оранжевая тема',
        purplePink: 'Фиолетово-розовая тема',
        neon: 'Неоновая тема',
        custom: 'Пользовательская тема'
      }
    },
    manageBlock: {
      beatSorting: 'Сортировка битов',
      bgImages: 'Фоновые изображения',
      socMedia: 'Социальные сети'
    },
    defaultBeatsSortingBlock: {
      successMessage: 'Изменения сохранены !'
    },
    uploadImagesBlock: {
      selectFileDescr: 'Выберите файл на компьютере',
      successfullyUploaded: 'Изображение успешно загружено',
      successfullyDeleted: 'Изображение успешно удалено',
      loadingMessage: 'Изображение загружается...',
      block1: {
        title: 'Большая обложка',
        helper: 'JPG или PNG, 1920х460 пикселей'
      },
      block2: {
        title: 'Логотип на большую обложку',
        helper: 'JPG или PNG, 465x84 пикселей'
      },
      block3: {
        title: 'Логотип в шапке',
        helper: 'JPG или PNG, 260х36 пикселей'
      },
      block4: {
        title: 'Логотип в футере',
        helper: 'JPG или PNG, 260х36 пикселей'
      },
      block5: {
        title: 'Favicon',
        helper: 'PNG, ICO или SVG, 50х50 пикселей'
      }
    },
    uploadContactsBlock: {
      setSuccessMessage(contact: string) {
        return `Контакт ${contact} успешно обновлен`
      }
    }
  },
  producerPage: {
    button: {
      follow: 'Подписаться',
      unfollow: 'Отписаться',
      following: 'Подписан'
    },
    beats: 'Битов',
    plays: 'Проигрываний',
    followers: 'Подписчиков',
    licenseBlock: 'Цены и условия лицензий',
    notifications: {
      successFollowMessage(beatmakerName: string) {
        return `Вы успешно подписались на ${beatmakerName}`
      },
      successUnFollowMessage(beatmakerName: string) {
        return `Вы успешно отписались от ${beatmakerName}`
      },
      uploadAvatar: 'Аватар успешно обновлен',
      uploadWallpaper: 'Background успешно обновлен'
    },
    avatarTooltip:
      'Для корректного отображения рекомендуем загружать квадратную картинку. Оптимальный размер изображения 300 Х 300 пикселей',
    wallpaperTooltip: 'Оптимальный размер изображения 1440 Х 338 пикселей'
  },
  infoPopup: {
    title: 'Войдите в свой аккаунт',
    descr:
      'Только зарегистрированные пользователи могут ставить лайки, добавлять биты в избранное и подписываться на битмейкеров. Войдите в свою учётную запись или создайте новую.',
    btn: {
      cancel: 'Отмена',
      authenticate: 'Авторизоваться'
    }
  },
  // COLORS
  c: {
    app: {
      heading: 'Общие настройки приложения',
      bg: 'Фон всего приложения (страница каталога и корзины)'
    },
    headerBlock: {
      heading: 'Шапка',
      bg: 'Фон шапки',
      logoBeatmakerName: 'Логотип (имя битмейкера)',
      links: 'Ссылки в меню',
      activeLink: 'Активная ссылка в меню',
      activeLinkLine: 'Активная ссылка в меню (нижняя линия)',
      cartIcon: 'Иконка корзины',
      cartCounter: 'Количество треков в корзине'
    },
    beatsBlock: {
      heading: 'Главная страница / Блок с битами',
      bg: 'Фон блока битов',
      title: 'Заголовок блока',
      btn: 'Кнопка "Показать больше"'
    },
    beatsBlockBeatPage: {
      heading: 'Страница бита / Блок с битами',
      bg: 'Фон блока битов',
      title: 'Заголовок блока'
    },
    beat: {
      heading: 'Бит',
      bg: 'Фон бита',
      activeBg: 'Фон активного бита',
      hoverBg: 'Фон бита при наведении',
      dividerLine: 'Разделительная линия',
      name: 'Название бита',
      descr: 'Описание бита',
      downloadBeatIcon: 'Иконка "Скачать бит"',
      shareBeatIcon: 'Иконка "Поделиться"',
      btnBuy: 'Кнопка "КУПИТЬ"',
      btnIconBuy: 'Иконка для кнопки "Купить"',
      btnInCart: 'Кнопка "В корзину"',
      btnIconInCart: 'Иконка для кнопки "В корзину"',
      dotsIcon: 'Иконка открытия доп. информации в моб. режиме',
      dropdownControlsBg: 'Выпадающее меню (моб. режим)',
      dropdownControlsIcons: 'Иконки в выпадающем меню (моб. режим)'
    },
    licenseBlock: {
      heading: 'Главная страница: / Блок лицензий',
      bg: 'Фон блока лицензий',
      title: 'Заголовок блока'
    },
    licenseBlockBeatPage: {
      heading: 'Страница бита / Блок лицензий',
      bg: 'Фон блока лицензий',
      title: 'Заголовок блока'
    },
    license: {
      heading: 'Лицензия',
      arrows: 'Стрелки',
      dots: 'Точки',
      topBlock: 'Верхний блок',
      mainBlock: 'Основной блок лицензии',
      bottomBlock: 'Нижний блок лицензии',
      listIcon: 'Иконка списка лицензий',
      discountIcon: 'Иконка скидки',
      btnShowAllBeats: 'Кнопка "К СПИСКУ БИТОВ"',
      btnShowAllBeatsIcon: 'Иконка для кнопки "К СПИСКУ БИТОВ"',
      btnAddToCart: 'Кнопка "ДОБАВИТЬ В КОРЗИНУ"',
      btnIconAddToCart: 'Иконка для кнопки "ДОБАВИТЬ В КОРЗИНУ"',
      btnGoToCart: 'Кнопка "В КОРЗИНУ"',
      btnIconGoToCart: 'Иконка для кнопки "В КОРЗИНУ"',
      recommended: 'Рекомендованная лицензия'
    },
    licenseBlockPopUp: {
      heading: 'Pop-up "Выбор лицензии"',
      bg: 'Фон блока лицензий',
      iconCancel: 'Иконка закрыть',
      licenseItem: 'Описание лицензии',
      licenseItemActive: 'Активная лицензия',
      licensePrice: 'Цвет цены',
      licenseCheckbox: 'Цвет чекбокса',
      licenseCheckboxActiveBorder: 'Граница активного чекбокса',
      licenseCheckboxArrowActive: 'Активный чекбокс',
      licensePopUpBtn: 'Кнопка',
      licensePopUpIcon: 'Иконка кнопки',
      licenseFeaturesList: 'Описание бита',
      licenseItemHover: 'Описание лицензии при наведении',
      licenseDescriptionDots: 'Цвет точек'
    },
    contactsBlock: {
      heading: 'Главная страница / Блок контактов',
      bg: 'Фон блока контактов',
      title: 'Заголовок',
      descr: 'Описание',
      requiredField: 'Описание обязательных полей',
      input: 'Поля ввода',
      inputPlaceholder: 'Placeholder для полей ввода',
      btn: 'Кнопка',
      btnIcon: 'Иконка для кнопки',
      socIcons: 'Иконки соц. сетей'
    },
    newsLetterBlock: {
      heading: 'Главная страница / Рассылка новостей',
      bg: 'Фон блока рассылки',
      title: 'Цвет заголовка',
      text: 'Цвет описания',
      input: 'Поля ввода',
      inputPlaceholder: 'Placeholder для полей ввода',
      btn: 'Кнопка',
      btnIcon: 'Иконка для кнопки'
    },
    footer: {
      heading: 'Футер',
      bg: 'Фон футера',
      logo: 'Логотип',
      info: 'Информация',
      linkGroupTitle: 'Название группы ссылок',
      links: 'Ссылки'
    },
    catalogPage: {
      heading: 'Страница "Каталог"',
      title: 'Цвет заголовка'
    },
    headerBlockBeatPage: {
      heading: 'Страница бита / Верхний блок',
      beatName: 'Название бита',
      playBtn: 'Кнопка "PLAY"',
      beatInfo: 'Информация о бите',
      beatInfoIcon: 'Информационные иконки',
      beatPrice: 'Стоимость бита',
      downloadBeatBtn: 'Кнопка "Скачать бит"',
      btnBuy: 'Кнопка "Купить"',
      btnIconBuy: 'Иконка для кнопки "Купить"',
      btnGoToCart: 'Кнопка "В корзину"',
      btnIconGoToCart: 'Иконка для кнопки "В корзину"'
    },
    cartPage: {
      heading: 'Страница "Корзина"',
      title: 'Заголовок',
      input: 'Поле ввода промокода',
      inputPlaceholder: 'Placeholder для поля ввода',
      successCouponMessage: 'Успешное сообщение',
      errorCouponMessage: 'Сообщение с ошибкой',
      btnCoupon: 'Кнопка "Применить"',
      btnCouponIcon: 'Иконка для кнопки "Применить"',
      disableBtnCoupon: 'Disable Кнопка "Применить"',
      disableBtnCouponIcon: 'Disable Иконка для кнопки "Применить"',
      beatPrice: 'Стоимость бита',
      discountPrice: 'Скидка',
      editLicenseIcon: 'Иконка "Изменить лицензию"',
      deleteBeatIcon: 'Иконка "Удалить бит"',
      btnBackToTracksPage: 'Кнопка "Вернуться на страницу треков"',
      btnBackToTracksPageIcon: 'Иконка для кнопки "Вернуться на страницу треков"'
    },
    cartPagePaymentBlock: {
      heading: 'Страница "Корзина" / Блок оплаты',
      bg: 'Фон блока',
      infoKey: 'Цвет суммы и скидки',
      infoValue: 'Цвет стоимости',
      infoDividerLine: 'Цвет разделительной линии',
      paymentTitle: 'К оплате',
      discountTitle: 'Сумма с учетом скидок',
      finalCost: 'Итоговая стоимость',
      btnConfirm: 'Кнопка "Подтвердить и оформить"',
      btnConfirmIcon: 'Иконка для кнопки "Подтвердить и оформить"'
    },
    cartPopup: {
      heading: 'Pop-up "Корзина"',
      bg: 'Фон блока',
      title: 'Заголовок',
      closeIcon: 'Иконка закрытия',
      beatName: 'Название бита',
      beatDescr: 'Описание бита',
      beatPrice: 'Стоимость бита',
      editLicenseIcon: 'Иконка "Изменить лицензию"',
      deleteBeatIcon: 'Иконка "Удалить бит"',
      discountPrice: 'Скидка',
      paymentTitle: 'К оплате',
      discountTitle: 'Сумма с учетом скидок',
      finalCost: 'Итоговая стоимость',
      btnGoToPayment: 'Кнопка "Перейти к оплате"',
      btnIconGoToPayment: 'Иконка для кнопки "Перейти к оплате"'
    },
    popUpConfirmOrder: {
      heading: 'Pop-up "Подтвердить и оформить заказ"',
      bg: 'Фон',
      popUpTitle: 'Заголовок',
      iconCancel: 'Иконка закрытия',
      price: 'Цена',
      description: 'Описание',
      input: 'Поле ввода',
      inputPlaceholder: 'Placeholder поля ввода',
      iconInfo: 'Иконка информации',
      tooltip: 'Тултип',
      button: 'Кнопка',
      btnIcon: 'Иконка кнопки',
      payPalEn: 'Paypal',
      requiredField: 'Описание обязательных полей'
    },
    shareTrackPopup: {
      heading: 'Pop-up "Поделиться треком"',
      bg: 'Фон блока',
      title: 'Заголовок',
      closeIcon: 'Иконка закрытия',
      beatName: 'Название бита',
      beatDescr: 'Описание бита',
      linkTitle: 'Ссылка на трек',
      linkInput: 'Поле co ссылкой',
      btnCopyLink: 'Кнопка "Копировать ссылку"',
      btnCopyLinkIcon: 'Иконка для кнопки "Копировать ссылку"',
      btnLinkCopied: 'Кнопка "Ссылка скопирована"',
      btnLinkCopiedIcon: 'Иконка для кнопки "Ссылка скопирована"'
    },
    userAgreement: {
      heading: 'Страница "Пользовательское соглашение"',
      bg: 'Фон',
      mainTitle: 'Главный заголовок',
      subTitle: 'Подзаголовок',
      link: 'Ссылка'
    },
    notFoundPage: {
      heading: 'Страница "404 - страница не найдена"',
      bg: 'Фон',
      mainImg: 'Главное изображение',
      mainTitle: 'Заголовок',
      descr: 'Текст',
      btn: 'Кнопка'
    },
    notFoundMessage: {
      heading: 'Страница "Каталог" / Биты не найдены',
      mainImg: 'Главное изображение',
      mainTitle: 'Заголовок',
      descr: 'Текст'
    },
    cookiePage: {
      heading: 'Страница "Политика cookie"',
      bg: 'Фон блока',
      mainTitle: 'Заголовок',
      subTitle: 'Подзаголовок',
      link: 'Ссылки'
    },
    cookieNotification: {
      heading: 'Pop-up "Cookie"',
      bg: 'Фон',
      link: 'Ссылка',
      button: 'Кнопка'
    },
    player: {
      heading: 'Плеер',
      bg: 'Фон блока',
      beatName: 'Название бита',
      beatDescr: 'Описание бита',
      progressBar: 'Цвет полосы трека',
      loadingBar: 'Цвет полосы загрузки',
      playedBeatBar: 'Цвет проигранного бита',
      marker: 'Цвет маркера',
      btnNextPrev: 'Предыдущий и следующий бит',
      btnPlay: 'Кнопка "Play"',
      soundHornIcon: 'Иконка громкости звука',
      downloadBeatIcon: 'Иконка "Скачать бит"',
      shareBeatIcon: 'Иконка "Поделиться"',
      btnBuy: 'Кнопка "Купить"',
      btnIconBuy: 'Иконка для кнопки "Купить"',
      btnGoToCart: 'Кнопка "В корзину"',
      btnIconGoToCart: 'Иконка для кнопки "В корзину"',
      toPlayerMobLgIcon: 'Иконка открытия открытия плеера в большой режим',
      soundProgressBar: 'Цвет всей полосы звука',
      soundFilledBar: 'Цвет установленного звука',
      soundThumb: 'Маркер звука'
    },
    selectMU: {
      heading: {
        lang: 'Футер / Селект изменения языка',
        findTracks: 'Страница "Каталог" / Селект',
        confirmPopup: 'Pop-up "Подтвердить и оформить заказ" / Селект'
      },
      select: {
        border: 'Цвет обводки селекта',
        borderHover: 'Цвет обводки при наведении',
        groupName: 'Цвет значения селекта',
        currentValue: 'Цвет выбранного элемента',
        arrow: 'Цвет стрелки'
      },
      options: {
        item: 'Элемент списка',
        selectedItem: 'Выбранный элемент списка',
        hoverItem: 'Элемент списка при наведении'
      }
    },
    inputMU: {
      heading: 'Страница "Каталог" / Инпут',
      border: 'Цвет обводки',
      borderHover: 'Цвет обводки при наведении',
      groupName: 'Цвет имени группы инпута',
      searchText: 'Цвет поискового текста',
      closeIcon: 'Иконка закрытия',
      tags: {
        list: 'Выпадающий список',
        item: 'Тег',
        notFoundText: 'Не найдено'
      }
    },
    loader: {
      heading: 'Прелоадер',
      common: 'Прелоадер'
    }
  }
}

const en: LgType = {
  pluralRules: new Intl.PluralRules('en'),
  pluralizeBeats({ count, isTotalBeatsCount }: PluralizeBeatsParams) {
    const form = this.pluralRules.select(count)
    switch (form) {
      case 'one':
        return isTotalBeatsCount ? `beat` : `${count} beat`
      case 'many':
        return isTotalBeatsCount ? `beats` : `${count} beats`
      default:
        return isTotalBeatsCount ? `beats` : `${count} beats`
    }
  },
  pluralizeTotalPlays(count: number): string {
    const form = this.pluralRules.select(count)
    switch (form) {
      case 'one':
        return `play`
      case 'many':
        return `plays`
      default:
        return `plays`
    }
  },
  pluralizeTotalFollowers(count: number): string {
    const form = this.pluralRules.select(count)
    switch (form) {
      case 'one':
        return `follower`
      case 'many':
        return `followers`
      default:
        return `followers`
    }
  },
  catalogPage: {
    headings: {
      allBeats: 'catalog - all beats',
      popularBeats: 'catalog - popular beats'
    },
    tabs: {
      tracks: 'Tracks',
      producers: 'Producers'
    }
  },
  freeBeatsPage: {
    headings: {
      freeBeats: 'Catalog - Free Beats'
    }
  },
  documentTitles: {
    allBeatmakersCatalog: 'Find beats from the hottest producers in the world | Beatmakers.tv',
    freeBeats: 'Catalog - Free beats | Beatmakers.tv',
    setBeatmakerPageTitle(beatmakerName: string) {
      return `${beatmakerName} | Beatmakers.tv`
    },
    setBeatmakerCatalogPageTitle(beatmakerName: string) {
      return `${beatmakerName} | Catalog | Beatmakers.tv`
    }
  },
  header: {
    home: 'Home',
    tracks: 'Beats',
    licenses: 'Licenses and prices',
    goods: 'Goods',
    services: 'Services',
    contacts: 'Contacts',
    pages: 'Site pages',
    cart: 'Cart',
    /* bmstv */
    catalog: 'Catalog',
    login: 'Login',
    favorites: 'Favorites',
    purchases: 'Purchases'
  },
  tracksBlock: {
    heading: 'Catalog',
    style: 'Style',
    mood: 'Mood',
    bpm: 'BPM',
    bpmDescription: 'Beats per minute',
    usingSamples: 'Using samples',
    similarTracks: 'You May Also Like These Beats',
    share: 'Share track',
    shareLink: 'Track link',
    downloadDrop: 'Download',
    addFavoriteDrop: 'Add favorites',
    shareDrop: 'Share'
  },
  producersBlock: {
    searchProducerPlaceholder: 'Producer name'
  },
  sortTrackBy: {
    rating: {
      label: 'Rating',
      freeBeatsOptions: {
        addedDateDesc: 'From newest to oldest',
        addedDateAsc: 'From oldest to newest',
        downloadsDesc: 'Popular (in descending)',
        downloadsAsc: 'Popular (in ascending)'
      },
      options: {
        newest: 'Newest',
        default: 'By default',
        top: 'Top'
      }
    },
    style: {
      label: 'Style',
      allStylesOption: 'All styles'
    },
    mood: {
      label: 'Mood',
      allMoodsOption: 'All moods'
    },
    tags: {
      label: 'Search by tag',
      notFound: 'Tags not found',
      remove: 'Remove tag'
    },
    key: {
      label: 'Key',
      allOption: 'Any key'
    },
    trackType: {
      label: 'Track type',
      allOption: 'Any type'
    },
    instruments: {
      label: 'Instruments'
    },
    bpm: {
      label: 'BPM'
    },
    price: {
      label: 'Price'
    },
    range: {
      from: 'from',
      to: 'to'
    },
    multiSelectMode: {
      and: 'all',
      or: 'any'
    },
    clearFilter: 'Clear filter',
    mobileFilters: {
      toggle: 'Filters'
    },
    beat: {
      label: 'Search by beat'
    },
    beatmaker: {
      label: 'Search by beatmaker',
      notFound: 'Beatmakers not found'
    },
    adminSettings: {
      label: 'Beat sorting',
      options: {
        default: 'By default (as in the admin panel)',
        top: 'By rating (starting with the best)'
      }
    }
  },
  youtubeBlock: {
    heading: 'Video on Youtube'
  },
  licenseBlock: {
    heading: 'Licensing information',
    getSlogan(buyedLicenseCount: number, bonusLicenseCount: number) {
      return `Buy ${buyedLicenseCount} beat and get ${bonusLicenseCount} more for free`
    },
    noLicenseInfo: 'No license information'
  },
  goodsBlock: {
    heading: 'Products'
  },
  servicesBlock: {
    heading: 'Services'
  },
  contactsBlock: {
    heading: 'Contacts',
    getDescription(beatmakerName: string) {
      return `Contact ${beatmakerName} using the contact form or social media links below`
    },
    requiredFieldDescription: 'Fields marked with an asterisk * are required before sending a message',
    socNetwork: 'Social network'
  },
  newsletterBlock: {
    heading: 'Newsletter',
    getDescription(beatmakerName: string) {
      return `Subscribe to ${beatmakerName} updates to stay informed about new beats and promotions going on`
    }
  },
  subscriptionBlock: {
    select1: '1 month',
    select2: '3 month',
    select3: '1 year',
    select4: '3 years'
  },
  footer: {
    getDescription(year: number) {
      return `Beatmakers.tv, ${year}. All rights reserved`
    },
    home: 'Home',
    tracks: 'Beats',
    licenses: 'Licenses',
    goods: 'Goods',
    services: 'Services',
    contacts: 'Contacts',
    pages: 'Pages',
    info: 'Info',
    'technical-support': 'Support',
    'user-agreement': 'Term of use',
    'cookie-policy': 'Cookie Policy',
    /*bmstv*/
    catalog: 'Catalog',
    'sign-in': 'Sign In',
    'sign-up': 'Sign Up',
    'account/favorites': 'My Favorites',
    'account/purchases': 'Purchased Beats',
    language: {
      title: 'Language',
      ru: 'Russian',
      en: 'English'
    }
  },
  cartPage: {
    cart: 'Cart',
    payText: 'To pay',
    amountDiscountText: 'Amount including discounts',
    orderText: 'Your order',
    coupon: 'Coupon',
    sum: 'Sum',
    discount: 'Discount',
    couponErrorEmpty: 'Empty coupon',
    couponErrorInvalid: 'The coupon you entered is invalid',
    couponSuccessMessage: 'Coupon successfully accepted',
    emptyCart: 'There is nothing in your cart yet',
    trackNotAvailable: 'Track not available',
    beatAddedToCart: 'Beat added to cart'
  },
  button: {
    buy: 'Buy',
    download: 'Download',
    showMore: 'Show More',
    showProducts: 'Show all products',
    showServices: 'Show all services',
    toChannel: 'Go to channel',
    toBeatList: 'Show all beats',
    toPayment: 'Go to the payment',
    toLogin: 'To login page',
    moreDetails: 'More details',
    sendMessage: 'Send message',
    subscribeNewsletter: 'Subscribe to the newsletter',
    payPurchase: 'Pay for the purchase',
    confirm: 'Confirm and checkout',
    apply: 'Apply',
    addToCart: 'Add to cart',
    goToCart: 'Go to cart',
    leaveComment: 'Leave a comment',
    priceFrom: 'From',
    understand: 'ok',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    save: 'Save',
    backToTracksPage: 'Back to the tracks page',
    updatePassword: 'Update password',
    updateEmail: 'Update email',
    resetPassword: 'Reset password',
    backToHome: 'Back to Home',
    search: 'Search'
  },
  forms: {
    name: 'Your name*',
    fullName: 'Full name*',
    email: 'Your email*',
    message: 'Your message*',
    password: 'Your password*',
    passportData: 'Passport data',
    waysOfPayment: 'Ways of payments',
    setEmptyErrorMessage(field: string) {
      return `The ${field} field mustn't be empty`
    },
    invalidEmail: 'Enter a valid email',
    successMessage: 'Fine. Your message has been sent. The owner of the page will answer you shortly. Thank!',
    subscribeMessage: 'You have successfully subscribed to the news',
    alreadySubscribeMessage: 'You are already subscribed',
    newPassword: 'New password',
    currentPassword: 'Current password',
    newEmail: 'New email',
    error: 'Form data is not valid, check all form fields for correctness',
    passwordsDontMatch: "The passwords don't match",
    passwordSuccessfullyChanged: 'Password successfully changed'
  },
  confirmationOrderPopup: {
    checkout: 'Checkout',
    total: 'Total',
    agreeLicenseTerm: 'By paying for the product you agree to the terms of the license',
    paymentSystem: 'Select the payment system',
    unknownError:
      'Something went wrong. Check again if your personal data is entered correctly. If the data is correct, then contact the administration of the site'
  },
  commentsBlock: {
    trackComments: 'Track comments'
  },
  textarea: {
    message: 'Enter your message'
  },
  availablePage: {
    title: 'Oops! Page is not available',
    descr:
      'We are very sorry for inconvenience. It looks like you’re trying to access a page that was has been deleted or never even existed'
  },
  pageNotFound: {
    title: 'Oops! Page not found!',
    descr:
      'We are very sorry for inconvenience. It looks like you’re trying to access a page that was has been deleted or never even existed'
  },
  noMatchSearch: {
    result: 'No matching search results',
    action: 'Check the spelling or try to remove filters'
  },
  cookiePolicyPopup: {
    description:
      'This website uses cookies to improve user experience. By using you consent to all cookies in accordance with our Cookie Policy.',
    btnAccept: 'Accept',
    btnReadMore: 'Learn more'
  },
  colorCustomizer: {
    designPage: 'Page design',
    textColor: 'Text color',
    bgColor: 'Background color',
    borderColor: 'Border color',
    themeSelect: {
      title: 'Select a theme',
      themes: {
        blackRed: 'Black-red theme',
        light: 'Light theme',
        blackOrange: 'Black-orange theme',
        purplePink: 'Purple-pink theme',
        neon: 'Neon theme',
        custom: 'Custom theme'
      }
    },
    manageBlock: {
      beatSorting: 'Beat sorting',
      bgImages: 'Background images',
      socMedia: 'Social media'
    },
    defaultBeatsSortingBlock: {
      successMessage: 'Changes saved !'
    },
    uploadImagesBlock: {
      selectFileDescr: 'Select the file on your computer',
      successfullyUploaded: 'Image successfully uploaded',
      successfullyDeleted: 'Image successfully deleted',
      loadingMessage: 'The image is uploading...',
      block1: {
        title: 'Large cover',
        helper: 'JPG or PNG, 1920х460 pixels'
      },
      block2: {
        title: 'Large cover logo',
        helper: 'JPG or PNG, 465х84 pixels'
      },
      block3: {
        title: 'Logo in the header',
        helper: 'JPG or PNG, 260х36 pixels'
      },
      block4: {
        title: 'Logo in the footer',
        helper: 'JPG or PNG, 260х36 pixels'
      },
      block5: {
        title: 'Favicon',
        helper: 'PNG, ICO or SVG, 50х50 pixels'
      }
    },
    uploadContactsBlock: {
      setSuccessMessage(contact: string) {
        return `The ${contact} contact was successfully updated`
      }
    }
  },
  producerPage: {
    button: {
      follow: 'Follow',
      unfollow: 'Unfollow',
      following: 'Following'
    },
    beats: 'Beats',
    plays: 'Plays',
    followers: 'Followers',
    licenseBlock: 'Licensing information',
    notifications: {
      successFollowMessage(beatmakerName: string) {
        return `You have successfully subscribed to ${beatmakerName}`
      },
      successUnFollowMessage(beatmakerName: string) {
        return `You have successfully unsubscribed from ${beatmakerName}`
      },
      uploadAvatar: 'The avatar has been successfully updated',
      uploadWallpaper: 'Background has been successfully updated'
    },
    avatarTooltip:
      'For proper display, we recommend uploading a square picture. The optimal image size is 300 by 300 pixels',
    wallpaperTooltip: 'The optimal image size is 1440 by 338 pixel'
  },
  infoPopup: {
    title: 'Log In your Account',
    descr:
      'Only registered users can like, add beats to favorites, and subscribe to beatmakers. Log in to your account or create a new one.',
    btn: {
      cancel: 'Cancel',
      authenticate: 'Authenticate'
    }
  },
  c: {
    app: {
      heading: 'General application settings',
      bg: 'Background of the whole application (catalog page and shopping cart)'
    },
    headerBlock: {
      heading: 'Header',
      bg: 'Header background',
      logoBeatmakerName: 'Logo (beatmaker name)',
      links: 'Menu links',
      activeLink: 'Active link',
      activeLinkLine: 'Active link (bottom line)',
      cartIcon: 'Cart icon',
      cartCounter: 'The number of tracks in the cart'
    },
    beatsBlock: {
      heading: 'Beats block / Main page',
      bg: 'Block background',
      title: 'Title',
      btn: 'Button "Show more"'
    },
    beatsBlockBeatPage: {
      heading: 'Beats block / Beat page',
      bg: 'Block background',
      title: 'Title'
    },
    beat: {
      heading: 'Beat',
      bg: 'Beat background',
      activeBg: 'Active beat background',
      hoverBg: 'Hover beat background',
      dividerLine: 'Divider line',
      name: 'Beat name',
      descr: 'Beat description',
      downloadBeatIcon: '"Download beat" icon',
      shareBeatIcon: '"Share beat" icon',
      btnBuy: 'Button "BUY"',
      btnIconBuy: 'Button "BUY" icon',
      btnInCart: 'Button "GO TO CART"',
      btnIconInCart: 'Button "GO TO CART" icon',
      dotsIcon: 'Icon for opening additional information in mobile mode',
      dropdownControlsBg: 'Dropdown menu (mob mode)',
      dropdownControlsIcons: 'Dropdown menu icons (mob mode)'
    },
    licenseBlock: {
      heading: 'Licenses block / Main page',
      bg: 'Licenses block background',
      title: 'Licenses block title'
    },
    licenseBlockBeatPage: {
      heading: 'Licenses block / Beat page',
      bg: 'Licenses block background',
      title: 'Licenses block title'
    },
    license: {
      heading: 'License',
      arrows: 'Arrows',
      dots: 'Dots',
      topBlock: 'Top block',
      mainBlock: 'Main block',
      bottomBlock: 'Bottom block',
      listIcon: 'Licence list icon',
      discountIcon: 'Discount icon',
      btnShowAllBeats: 'Button "SHOW ALL BEATS"',
      btnShowAllBeatsIcon: 'Button "SHOW ALL BEATS" icon',
      btnAddToCart: 'Button "ADD TO CART"',
      btnIconAddToCart: 'Button "ADD TO CART" icon',
      btnGoToCart: 'Button "GO TO CART"',
      btnIconGoToCart: 'Button "GO TO CART" icon',
      recommended: 'Recommended license'
    },
    licenseBlockPopUp: {
      heading: 'Pop Up Licenses',
      bg: 'Licenses background',
      iconCancel: 'Close icon',
      licenseItem: 'License item',
      licenseItemActive: 'Active license',
      licensePrice: 'Price color',
      licenseCheckbox: 'Checkbox color',
      licenseCheckboxActiveBorder: 'Checkbox border',
      licenseCheckboxArrowActive: 'Checkbox active color',
      licensePopUpBtn: 'Button',
      licensePopUpIcon: 'Icon Button',
      licenseFeaturesList: 'Beat description',
      licenseItemHover: 'License item hover',
      licenseDescriptionDots: 'Dots color'
    },
    contactsBlock: {
      heading: 'Contacts',
      bg: 'Contacts background',
      title: 'Title',
      descr: 'Description',
      requiredField: 'Description of required fields',
      input: 'Input fields',
      inputPlaceholder: 'Input placeholder',
      btn: 'Button',
      btnIcon: 'Button icon',
      socIcons: 'Social icons'
    },
    newsLetterBlock: {
      heading: 'Newsletter',
      bg: 'Newsletter background',
      title: 'Header color',
      text: 'Description color',
      input: 'Input fields',
      inputPlaceholder: 'Input placeholder',
      btn: 'Button',
      btnIcon: 'Button icon'
    },
    footer: {
      heading: 'Footer',
      bg: 'Footer background',
      logo: 'Logotype',
      info: 'Information text',
      linkGroupTitle: 'Link group name',
      links: 'Links'
    },
    catalogPage: {
      heading: 'Catalog page',
      title: 'Header color'
    },
    headerBlockBeatPage: {
      heading: 'Top block / Beat page',
      beatName: 'Beat name',
      playBtn: 'Button "PLAY"',
      beatInfo: 'Beat information',
      beatInfoIcon: 'Beat information icons',
      beatPrice: 'Стоимость бита',
      downloadBeatBtn: 'Кнопка "Скачать бит"',
      btnBuy: 'Button "Buy"',
      btnIconBuy: 'Button "Buy" icon',
      btnGoToCart: 'Button "Go to cart"',
      btnIconGoToCart: 'Button "Go to cart" icon'
    },
    cartPage: {
      heading: 'Cart page',
      title: 'Title',
      input: 'Coupon input field',
      inputPlaceholder: 'Coupon input field placeholder',
      successCouponMessage: 'Success coupon message',
      errorCouponMessage: 'Error coupon message',
      btnCoupon: 'Apply button',
      btnCouponIcon: 'Apply button icon',
      disableBtnCoupon: 'Disabled Apply button',
      disableBtnCouponIcon: 'Disabled Apply button icon',
      beatPrice: 'Beat price',
      discountPrice: 'Discount',
      editLicenseIcon: 'Edit license icon',
      deleteBeatIcon: 'Delete beat icon',
      btnBackToTracksPage: 'Return to track page button',
      btnBackToTracksPageIcon: 'Return to track page button icon'
    },
    cartPagePaymentBlock: {
      heading: 'Checkout page / Payment block',
      bg: 'Background block',
      infoKey: 'Sum and discount title color',
      infoValue: 'Sum and discount cash color',
      infoDividerLine: 'Divider line color',
      paymentTitle: 'To pay',
      discountTitle: 'Amount including discounts',
      finalCost: 'Final cost',
      btnConfirm: 'Confirm and checkout button',
      btnConfirmIcon: 'Confirm and checkout button icon'
    },
    cartPopup: {
      heading: 'Cart pop-up',
      bg: 'Pop-up background',
      title: 'Title',
      closeIcon: 'Close icon',
      beatName: 'Beat name',
      beatDescr: 'Beat description',
      beatPrice: 'Beat price',
      editLicenseIcon: 'Edit license icon',
      deleteBeatIcon: 'Delete beat icon',
      discountPrice: 'Discount',
      paymentTitle: 'To pay',
      discountTitle: 'Amount including discounts',
      finalCost: 'Final cost',
      btnGoToPayment: 'Button "Go to the payment"',
      btnIconGoToPayment: 'Button "Go to the payment" icon'
    },
    popUpConfirmOrder: {
      heading: 'PopUp confirm order',
      bg: 'Background',
      popUpTitle: 'Title',
      iconCancel: 'Close icon',
      price: 'Price',
      description: 'Description',
      input: 'Input',
      inputPlaceholder: 'Placeholder',
      iconInfo: 'Information icon',
      tooltip: 'Tooltip',
      button: 'Button',
      btnIcon: 'Button icon',
      payPalEn: 'Paypal',
      requiredField: 'Description of required fields'
    },
    userAgreement: {
      heading: 'User Agreement',
      bg: 'Background',
      mainTitle: 'Main title',
      subTitle: 'Subtitle',
      link: 'Link'
    },
    notFoundPage: {
      heading: 'Page not found',
      bg: 'Background',
      mainImg: 'Main image',
      mainTitle: 'Main title',
      descr: 'Description',
      btn: 'Button'
    },
    notFoundMessage: {
      heading: 'Beats not found',
      mainImg: 'Main image',
      mainTitle: 'Main title',
      descr: 'Description'
    },
    shareTrackPopup: {
      heading: 'Share track pop-up',
      bg: 'Pop-up background',
      title: 'Title',
      closeIcon: 'Close icon',
      beatName: 'Beat name',
      beatDescr: 'Beat description',
      linkTitle: 'Track link',
      linkInput: 'Link input',
      btnCopyLink: '"Copy link" button',
      btnCopyLinkIcon: 'Icon for "Copy link" button',
      btnLinkCopied: '"Link copied" button',
      btnLinkCopiedIcon: 'Icon for "Link copied" button'
    },
    cookiePage: {
      heading: 'Cookies Page',
      bg: 'Background',
      mainTitle: 'Title',
      subTitle: 'Subtitle',
      link: 'Links'
    },
    cookieNotification: {
      heading: 'Cookie notification',
      bg: 'Background',
      link: 'Link',
      button: 'Button'
    },
    player: {
      heading: 'Player',
      bg: 'Block background',
      beatName: 'Beat name',
      beatDescr: 'Beat description',
      progressBar: 'Color of the track bar',
      loadingBar: 'Color of loading bar',
      playedBeatBar: 'Color of beat played',
      marker: 'Color of marker',
      btnNextPrev: 'Prev and next button',
      btnPlay: 'Play button',
      soundHornIcon: '"Sound horn" icon',
      downloadBeatIcon: '"Download beat" icon',
      shareBeatIcon: '"Share beat" icon',
      btnBuy: 'Button "Buy"',
      btnIconBuy: 'Button "Buy" icon',
      btnGoToCart: 'Button "Go to cart"',
      btnIconGoToCart: 'Button "Go to cart" icon',
      toPlayerMobLgIcon: 'Icon for opening the player in large mode',
      soundProgressBar: 'The color of the entire sound bar',
      soundFilledBar: 'The color of the set sound',
      soundThumb: 'Color of marker'
    },
    selectMU: {
      heading: {
        lang: 'Select / lang',
        findTracks: 'Select / Catalog page',
        confirmPopup: 'Селект / Confirm pop-up'
      },
      select: {
        border: "The color of the selection's stroke",
        borderHover: 'The color of the border when hovered',
        groupName: 'The color of the select value',
        currentValue: 'The color of the selected item',
        arrow: 'The color of the arrow'
      },
      options: {
        item: 'List item',
        selectedItem: 'Selected list item',
        hoverItem: 'List item when hovered'
      }
    },
    inputMU: {
      heading: 'Input / Catalog page',
      border: 'Border color',
      borderHover: 'Border color on hover',
      groupName: 'Input group name color',
      searchText: 'Search text',
      closeIcon: 'Close icon',
      tags: {
        list: 'Drop-down list',
        item: 'Tag',
        notFoundText: 'Not found'
      }
    },
    loader: {
      heading: 'Loaders',
      common: 'Loader'
    }
  }
}
